"use client"

import Link from "next/link"
import Image from "next/image"
import { useState } from "react"
import { Plane, Heart, Maximize2, ArrowRight, Clock, Activity, Globe, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { SiteNav } from "@/components/site-nav"

// ─── Brand colours (exact hex) ─────────────────────────────────────────────────
const BRAND = {
  darkBlue: "#174A84",
  midBlue: "#2A70B0",
  lightBlue: "#3A6FCC",
  gold: "#F4B333",
  red: "#DF463B",
  grey: "#9B9EA0",
  silver: "#E1E3E4",
  white: "#FFFFFF",
  nearBlack: "#1A1A2E",
} as const

// Body text on light surfaces (passes WCAG AA)
const BODY = "#3A3A4E"
// Muted helper text (form labels, secondary copy) — faded gray hierarchy
const MUTED = "#6B7280"
const LABEL = "#4B5563"

// ─── Quote Form ───────────────────────────────────────────────────────────────
function QuoteForm() {
  const [submitted, setSubmitted] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div
        className="p-8 text-center border"
        style={{ backgroundColor: BRAND.white, borderColor: BRAND.silver }}
      >
        <div
          className="mx-auto w-12 h-12 flex items-center justify-center mb-4"
          style={{ backgroundColor: BRAND.darkBlue }}
        >
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3
          className="text-lg font-heading font-semibold mb-2"
          style={{ color: BRAND.darkBlue }}
        >
          Request Received
        </h3>
        <p className="text-base" style={{ color: BODY }}>
          A personal medical flight advisor will contact you within minutes.
          <br />
          <span className="font-semibold" style={{ color: BRAND.darkBlue }}>
            (800) 426-6557
          </span>
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="p-6 space-y-4"
      style={{
        backgroundColor: BRAND.white,
        border: `1px solid ${BRAND.silver}`,
        boxShadow: "0 8px 28px rgba(23, 74, 132, 0.10)",
        borderRadius: 4,
      }}
    >
      {/* Form header */}
      <div className="pb-4 mb-1 border-b" style={{ borderColor: BRAND.silver }}>
        <h3
          className="font-heading font-semibold text-xl leading-tight mb-1"
          style={{ color: BRAND.darkBlue }}
        >
          Request a Medical Flight Quote
        </h3>
        <p className="text-sm font-light" style={{ color: MUTED }}>
          24/7 — We&apos;re Ready to Respond
        </p>
      </div>

      <div className="space-y-3.5">
        {/* Patient Name */}
        <div>
          <Label
            htmlFor="patient-name"
            className="text-sm font-normal mb-1.5 block normal-case tracking-normal"
            style={{ color: MUTED }}
          >
            Patient Name
          </Label>
          <Input
            id="patient-name"
            required
            placeholder="Full name"
            className="text-base h-11"
          />
        </div>

        {/* From + To — side by side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label
              htmlFor="from"
              className="text-sm font-normal mb-1.5 block normal-case tracking-normal"
              style={{ color: MUTED }}
            >
              From
            </Label>
            <Input
              id="from"
              required
              placeholder="City, State or Facility"
              className="text-base h-11"
            />
          </div>
          <div>
            <Label
              htmlFor="to"
              className="text-sm font-normal mb-1.5 block normal-case tracking-normal"
              style={{ color: MUTED }}
            >
              To
            </Label>
            <Input
              id="to"
              required
              placeholder="City, State or Facility"
              className="text-base h-11"
            />
          </div>
        </div>

        {/* Condition */}
        <div>
          <Label
            htmlFor="condition"
            className="text-sm font-normal mb-1.5 block normal-case tracking-normal"
            style={{ color: MUTED }}
          >
            Patient Condition / Needs
          </Label>
          <Textarea
            id="condition"
            rows={2}
            placeholder="Brief description of medical needs"
            className="resize-none text-base"
          />
        </div>

        {/* Your Name */}
        <div>
          <Label
            htmlFor="your-name"
            className="text-sm font-normal mb-1.5 block normal-case tracking-normal"
            style={{ color: MUTED }}
          >
            Your Name
          </Label>
          <Input
            id="your-name"
            required
            placeholder="Your full name"
            className="text-base h-11"
          />
        </div>

        {/* Phone + Email — side by side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label
              htmlFor="phone"
              className="text-sm font-normal mb-1.5 block normal-case tracking-normal"
              style={{ color: MUTED }}
            >
              Phone Number
            </Label>
            <Input
              id="phone"
              type="tel"
              required
              placeholder="(800) 000-0000"
              className="text-base h-11"
            />
          </div>
          <div>
            <Label
              htmlFor="email"
              className="text-sm font-normal mb-1.5 block normal-case tracking-normal"
              style={{ color: MUTED }}
            >
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              required
              placeholder="you@example.com"
              className="text-base h-11"
            />
          </div>
        </div>
      </div>

      <Button
        type="submit"
        className="w-full font-heading font-medium text-base py-6 transition-all duration-300 ease-out mt-2 hover:scale-[1.02] hover:shadow-lg active:scale-[0.99] cta-static"
        style={{
          backgroundColor: BRAND.lightBlue,
          color: BRAND.white,
          borderRadius: 6,
        }}
      >
        Get Medical Flight Costs Now
      </Button>

      <p
        className="text-center text-sm"
        style={{ color: MUTED }}
      >
        Your information is secure and confidential. HIPAA Compliant.
      </p>
    </form>
  )
}

// ─── Navbar ────────────────────────────────────────────────────────────────────
// Uses the shared <SiteNav /> component (see components/site-nav.tsx).

// ─── Section 1: Hero ──────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section
      className="relative bg-white lg:overflow-hidden"
      style={{ paddingBottom: 80 }}
    >
      {/* DESKTOP — masked video well flush to the far-right edge of the hero,
          full section height. The .hero-video-mask clips the container so the
          right edge is flat (hard against the viewport) and only the top-left
          & bottom-left corners are rounded — no gap on the right. */}
      <div
        className="hidden lg:block absolute top-0 bottom-0 right-0 hero-video-mask"
        style={{ width: "48%", zIndex: 1 }}
        aria-hidden="true"
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="/videos/hero-flight.mp4" type="video/mp4" />
        </video>
      </div>

      {/* MOBILE+DESKTOP — copy block (left column).
          On lg+ this column is constrained to the LEFT ~54% of the hero so it
          can never overlap the right-hand video well — letting the video read
          as one uninterrupted panel that reaches the far-right edge. */}
      <div className="relative z-10 w-full lg:w-[54%] pt-8 sm:pt-10 md:pt-28 lg:pt-32 pb-6 md:pb-12">
        <div className="px-5 sm:px-6 md:pl-10 lg:pl-14">
          <div className="max-w-xl space-y-5 md:space-y-7">
            <p
              className="font-heading font-medium text-xs sm:text-sm tracking-[0.18em]"
              style={{ color: BRAND.darkBlue }}
            >
              Fixed-Wing Only. We Do Not Operate Helicopters.
            </p>

            <h1
              className="font-heading font-extrabold text-[2.25rem] leading-[1.02] sm:text-5xl md:text-6xl sm:leading-[0.98] tracking-tight"
              style={{ color: BRAND.darkBlue }}
            >
              Critical Care.
              <br />
              <span style={{ color: BRAND.nearBlack }}>Without Limits.</span>
            </h1>

            <p
              className="text-base sm:text-lg md:text-xl leading-relaxed font-light max-w-lg"
              style={{ color: BRAND.nearBlack }}
            >
              Specializing in fixed-wing air ambulance flights that deliver ICU-level care
              and get patients where they need to be safely, quickly, and directly.
            </p>
          </div>
        </div>
      </div>

      {/* MOBILE + TABLET — video rendered below the copy (no mask, full width) */}
      <div className="relative z-10 block lg:hidden px-5 sm:px-6 pb-6 w-full">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-auto"
          style={{ borderRadius: 10, display: "block" }}
        >
          <source src="/videos/hero-flight.mp4" type="video/mp4" />
        </video>
      </div>

      {/* CTAs — left column, below the copy (constrained to left 54% on lg+
          so they never overlap the right-hand video well) */}
      <div className="relative z-10 w-full lg:w-[54%] pb-10 md:pb-12">
        <div className="px-5 sm:px-6 md:pl-10 lg:pl-14">
          <div className="flex flex-col sm:flex-row gap-3 pt-2 max-w-xl">
            <a
              href="tel:8004266557"
              className="font-heading font-medium text-base px-8 py-4 transition-all duration-300 ease-out text-center hover:scale-[1.03] hover:shadow-lg active:scale-[0.98] cta-lightblue"
              style={{ backgroundColor: BRAND.lightBlue, color: BRAND.white, borderRadius: 6 }}
            >
              Call (800) 426-6557 — 24/7
            </a>
            <a
              href="#quote-form"
              className="font-heading font-medium text-base px-8 py-4 transition-all duration-300 ease-out text-center hover:scale-[1.03] hover:shadow-lg active:scale-[0.98] cta-outline"
              style={{
                backgroundColor: BRAND.white,
                color: BRAND.lightBlue,
                border: `1.5px solid ${BRAND.lightBlue}`,
                borderRadius: 6,
              }}
            >
              Request a Quote
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Section 1b: Peeking Quote Form Card (multi-step) ────────────────────────
function SkinnyFormBar() {
  const [step, setStep] = useState<1 | 2>(1)
  const [submitted, setSubmitted] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
  }

  function nextStep(e: React.FormEvent) {
    e.preventDefault()
    setStep((s) => (s < 2 ? ((s + 1) as 1 | 2) : s))
  }

  function prevStep() {
    setStep((s) => (s > 1 ? ((s - 1) as 1 | 2) : s))
  }

  if (submitted) {
    return (
      <section
        id="quote-form"
        className="relative z-20 px-5 sm:px-6 form-peek-section"
        style={{ paddingBottom: 24, paddingTop: 16, backgroundColor: BRAND.white }}
      >
        <div
          className="mx-auto flex items-center justify-center px-5 py-6 sm:px-8 sm:py-7"
          style={{
            maxWidth: 1000,
            backgroundColor: BRAND.white,
            borderRadius: 12,
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
          }}
        >
          <div className="flex items-center gap-4">
            <div
              className="w-10 h-10 flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: BRAND.darkBlue, borderRadius: 8 }}
            >
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p
                className="font-heading font-semibold text-sm md:text-base"
                style={{ color: BRAND.darkBlue }}
              >
                Request Received — A personal medical flight advisor will contact you within minutes at (800) 426-6557.
              </p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section
      id="quote-form"
      className="relative z-20 px-5 sm:px-6 form-peek-section"
      style={{ paddingBottom: 32, paddingTop: 16, backgroundColor: BRAND.white }}
    >
      <form
        onSubmit={handleSubmit}
        className="mx-auto px-5 py-6 sm:px-7"
        style={{
          maxWidth: 1000,
          backgroundColor: BRAND.white,
          borderRadius: 12,
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
        }}
      >
        {/* Header + step indicator */}
        <div className="pb-4 mb-5 border-b" style={{ borderColor: BRAND.silver }}>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h3
                className="font-heading font-extrabold text-xl md:text-2xl leading-tight"
                style={{ color: BRAND.darkBlue }}
              >
                Request a Medical Flight Quote
              </h3>
              <p className="text-xs font-light mt-1" style={{ color: MUTED }}>
                24/7 — We&apos;re Ready to Respond
              </p>
            </div>
            <div className="flex items-center gap-2">
              {[1, 2].map((n) => (
                <div
                  key={n}
                  className="flex items-center gap-1.5"
                >
                  <div
                    className="w-6 h-6 flex items-center justify-center font-heading font-bold text-xs"
                    style={{
                      backgroundColor: n <= step ? BRAND.darkBlue : BRAND.silver,
                      color: n <= step ? BRAND.white : BRAND.darkBlue,
                      borderRadius: 4,
                    }}
                  >
                    {n}
                  </div>
                  {n < 2 && (
                    <div
                      className="w-6 h-[2px]"
                      style={{ backgroundColor: n < step ? BRAND.darkBlue : BRAND.silver }}
                    />
                  )}
                </div>
              ))}
              <span
                className="ml-2 font-heading font-medium text-xs tracking-wide"
                style={{ color: MUTED }}
              >
                Step {step} of 2
              </span>
            </div>
          </div>
        </div>

        {/* STEP 1 — Patient Name | Your Name | Patient Condition | Next (right) */}
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5 mb-5">
            <div className="md:col-span-3">
              <Label
                htmlFor="patient-name"
                className="text-xs font-normal mb-1 block normal-case tracking-normal"
                style={{ color: LABEL }}
              >
                Patient Name
              </Label>
              <Input
                id="patient-name"
                required
                placeholder="Full name"
                className="text-sm h-10"
              />
            </div>
            <div className="md:col-span-3">
              <Label
                htmlFor="your-name"
                className="text-xs font-normal mb-1 block normal-case tracking-normal"
                style={{ color: LABEL }}
              >
                Your Name
              </Label>
              <Input
                id="your-name"
                required
                placeholder="Your full name"
                className="text-sm h-10"
              />
            </div>
            <div className="md:col-span-3">
              <Label
                htmlFor="condition"
                className="text-xs font-normal mb-1 block normal-case tracking-normal"
                style={{ color: LABEL }}
              >
                Patient Condition / Needs
              </Label>
              <Input
                id="condition"
                required
                placeholder="Brief description"
                className="text-sm h-10"
              />
            </div>
            <div className="md:col-span-3">
              {/* Spacer label so the Next button aligns with the input boxes */}
              <Label
                className="text-xs font-normal mb-1 block normal-case tracking-normal invisible"
                aria-hidden="true"
              >
                &nbsp;
              </Label>
              <Button
                type="button"
                onClick={nextStep}
                className="w-full font-heading font-bold text-sm tracking-[0.05em] h-10 transition-all duration-300 ease-out hover:scale-[1.01] hover:shadow-lg active:scale-[0.99] cta-lightblue"
                style={{
                  backgroundColor: BRAND.lightBlue,
                  color: BRAND.white,
                  borderRadius: 6,
                  letterSpacing: "0.05em",
                }}
              >
                Next →
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 — From | To | Phone | Email | Submit (right) */}
        {step === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5 mb-4">
            <div className="md:col-span-3">
              <Label
                htmlFor="from"
                className="text-xs font-normal mb-1 block normal-case tracking-normal"
                style={{ color: LABEL }}
              >
                From
              </Label>
              <Input
                id="from"
                required
                placeholder="City, State or Facility"
                className="text-sm h-10"
              />
            </div>
            <div className="md:col-span-3">
              <Label
                htmlFor="to"
                className="text-xs font-normal mb-1 block normal-case tracking-normal"
                style={{ color: LABEL }}
              >
                To
              </Label>
              <Input
                id="to"
                required
                placeholder="City, State or Facility"
                className="text-sm h-10"
              />
            </div>
            <div className="md:col-span-2">
              <Label
                htmlFor="phone"
                className="text-xs font-normal mb-1 block normal-case tracking-normal"
                style={{ color: LABEL }}
              >
                Phone Number
              </Label>
              <Input
                id="phone"
                type="tel"
                required
                placeholder="(800) 000-0000"
                className="text-sm h-10"
              />
            </div>
            <div className="md:col-span-2">
              <Label
                htmlFor="email"
                className="text-xs font-normal mb-1 block normal-case tracking-normal"
                style={{ color: LABEL }}
              >
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="you@example.com"
                className="text-sm h-10"
              />
            </div>
            <div className="md:col-span-2">
              {/* Spacer label so the Submit button aligns with the input boxes */}
              <Label
                className="text-xs font-normal mb-1 block normal-case tracking-normal invisible"
                aria-hidden="true"
              >
                &nbsp;
              </Label>
              <Button
                type="submit"
                className="w-full font-heading font-bold text-sm tracking-[0.05em] h-10 transition-all duration-300 ease-out hover:scale-[1.01] hover:shadow-lg active:scale-[0.99] cta-lightblue"
                style={{
                  backgroundColor: BRAND.lightBlue,
                  color: BRAND.white,
                  borderRadius: 6,
                  letterSpacing: "0.05em",
                }}
              >
                GET MEDICAL FLIGHT COSTS NOW
              </Button>
            </div>
          </div>
        )}

        {/* Back link — only on step 2 */}
        {step > 1 && (
          <div className="flex items-center">
            <button
              type="button"
              onClick={prevStep}
              className="font-heading font-medium text-xs tracking-wide flex items-center gap-1.5 nav-link-hover"
              style={{ color: BRAND.darkBlue }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>
        )}

        {/* HIPAA footer */}
        <p
          className="text-center text-xs mt-5"
          style={{ color: BRAND.grey }}
        >
          Your information is secure and confidential. HIPAA Compliant.
        </p>
      </form>
    </section>
  )
}

// ─── Section 2: Trust Bar — 4 white icon cards with blue hover ────────────────
function TrustBar() {
  const items = [
    {
      label: "24/7/365 Always Available",
      icon: Clock,
    },
    {
      label: "ICU-Level Care In-Flight",
      icon: Activity,
    },
    {
      label: "Nationwide & Worldwide Reach",
      icon: Globe,
    },
    {
      label: "Safe. Direct. Dependable.",
      icon: ShieldCheck,
    },
  ]

  return (
    <section
      className="py-8 md:py-12 relative"
      style={{ backgroundColor: BRAND.white }}
    >
      <div className="mx-auto px-5 sm:px-6" style={{ maxWidth: 1280 }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
          {items.map((item, i) => {
            const Icon = item.icon
            return (
              <div
                key={i}
                className="why-card trust-card flex items-center gap-4 px-5 py-5"
                style={{ borderRadius: 12 }}
              >
                <div
                  className="flex-shrink-0 w-12 h-12 flex items-center justify-center why-card-icon"
                  style={{ borderRadius: 10 }}
                >
                  <Icon
                    className="w-6 h-6"
                    strokeWidth={1.75}
                  />
                </div>
                <p
                  className="font-heading font-bold text-xs md:text-sm leading-snug why-card-title"
                >
                  {item.label}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// ─── Section 3: Why Choose Us — cards on left, image-swap on right ───────────
function WhyUsSection() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null)
  const reasons = [
    {
      title: "Faster Long-Distance Transport",
      body: "Cover more ground in less time with jet-speed fixed-wing aircraft designed for long-haul medical transfers.",
      image: "/images/jet-engine-closeup-against-modern-building-2026-01-11-09-23-58-utc-1.jpg",
      icon: Plane,
    },
    {
      title: "Greater Comfort & Stability",
      body: "Smoother flights and pressurised cabins reduce stress on critically ill patients throughout the journey.",
      image: "/images/elegant-interior-of-a-private-jet-airplane-2026-01-05-04-49-11-utc-1.jpg",
      icon: Heart,
    },
    {
      title: "More Space Onboard",
      body: "Advanced equipment and specialised medical teams have the room they need to deliver ICU-level care.",
      image: "/images/small-aircraft-on-airfield-2026-03-25-01-06-24-utc-1.jpg",
      icon: Maximize2,
    },
    {
      title: "Direct, Non-Stop Flights",
      body: "Fewer stops, fewer risks, better outcomes — your loved one gets to the right facility, faster.",
      image: "/images/scenic-close-bottom-view-modern-luxury-expensive-w-2026-03-17-14-40-54-utc-1.jpg",
      icon: ArrowRight,
    },
  ]

  return (
    <section
      id="why-us"
      className="py-16 md:py-28 relative"
      style={{ backgroundColor: BRAND.white }}
    >
      <div className="mx-auto px-5 sm:px-6" style={{ maxWidth: 1280 }}>
        {/* Centred header */}
        <div className="text-center mb-10 md:mb-16 max-w-3xl mx-auto space-y-4 md:space-y-5">
          <h2
            className="font-heading font-extrabold text-3xl md:text-4xl leading-[1.02] tracking-tight"
            style={{ color: BRAND.darkBlue }}
          >
            Why Fixed-Wing Medical Flights?
          </h2>
          <p
            className="text-sm md:text-base leading-relaxed"
            style={{ color: BRAND.nearBlack }}
          >
            Fixed-wing aircraft offer the speed, range, and stability needed for critical patients —
            especially for long distances and time-sensitive transfers.
          </p>
        </div>

        {/* 2-col: 4 stacked cards on left, large image on right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          {/* Left — 4 cards stacked */}
          <div
            className="flex flex-col gap-4"
            onMouseLeave={() => setActiveIdx(null)}
          >
            {reasons.map((r, i) => {
              const Icon = r.icon
              const isActive = i === activeIdx
              return (
                <div
                  key={i}
                  onMouseEnter={() => setActiveIdx(i)}
                  onFocus={() => setActiveIdx(i)}
                  onBlur={() => setActiveIdx(null)}
                  tabIndex={0}
                  className={`why-card-interactive flex items-start gap-4 p-5 cursor-pointer ${isActive ? "is-active" : ""}`}
                  style={{ borderRadius: 12 }}
                >
                  <div
                    className="hidden md:flex flex-shrink-0 w-12 h-12 items-center justify-center why-card-icon"
                    style={{ borderRadius: 10 }}
                  >
                    <Icon
                      className="w-6 h-6"
                      strokeWidth={1.75}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2.5 mb-1">
                      <span
                        className="font-heading font-extrabold text-base tabular-nums why-card-num"
                      >
                        0{i + 1}
                      </span>
                      <h3
                        className="font-heading font-bold text-base md:text-lg leading-snug why-card-title"
                      >
                        {r.title}
                      </h3>
                    </div>
                    <p
                      className="text-sm leading-relaxed why-card-body"
                    >
                      {r.body}
                    </p>
                    {/* Mobile-only image well — replaces the desktop image-swap */}
                    <div
                      className="block lg:hidden mt-3 overflow-hidden"
                      style={{ borderRadius: 8, aspectRatio: "16 / 10" }}
                    >
                      <img
                        src={r.image}
                        alt={r.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Right — image swap (desktop only). On mobile this column collapses and the image is hidden. */}
          <div
            className="relative overflow-hidden bg-slate-200 hidden lg:block"
            style={{
              borderRadius: 12,
              position: "relative",
              minHeight: 520,
            }}
          >
            {/* Stacked <img> tags crossfading on opacity */}
            {reasons.map((r, i) => {
              const isVisible = activeIdx === null ? i === 0 : i === activeIdx
              return (
                <img
                  key={i}
                  src={r.image}
                  alt={r.title}
                  loading="eager"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover why-card-image"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    zIndex: isVisible ? 1 : 0,
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                />
              )
            })}
            {/* Subtle dark overlay for legibility */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `linear-gradient(180deg, transparent 50%, ${BRAND.darkBlue}cc 100%)`,
                zIndex: 2,
              }}
            />
          </div>
        </div>

        {/* Closing statement — centred inside a dark-blue box */}
        <div className="mt-10 md:mt-16 flex justify-center">
          <div
            className="px-6 py-5 sm:px-10 sm:py-6 max-w-3xl text-center"
            style={{
              backgroundColor: BRAND.darkBlue,
              borderRadius: 12,
              boxShadow: "0 8px 28px rgba(23, 74, 132, 0.18)",
            }}
          >
            <p
              className="font-heading font-bold text-base md:text-xl leading-snug"
              style={{ color: BRAND.white }}
            >
              No helicopters. No compromises. Just critical care, delivered.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Section 4: The Process ───────────────────────────────────────────────────
function ProcessSection() {
  const steps = [
    {
      index: "1",
      title: "Request",
      body: "Call or submit a request anytime, day or night. Your personal medical flight advisor will respond immediately.",
      image: "/images/process-1-request-724078.png",
    },
    {
      index: "2",
      title: "Coordinate",
      body: "Our personal medical flight advisors assess, plan, and coordinate every detail of the flight.",
      image: "/images/process-2-coordinate-724088.png",
    },
    {
      index: "3",
      title: "Transport",
      body: "Our critical care team transports your patient safely to their destination.",
      image: "/images/process-3-transport-724097.png",
    },
    {
      index: "4",
      title: "Billing & Insurance",
      body: "Our dedicated insurance team verifies coverage and manages all billing and reimbursement details, working directly with your provider so you can focus on what matters most.",
      image: "/images/process-4-billing-093620.png",
    },
  ]

  return (
    <section id="process" className="py-16 md:py-28 bg-white relative overflow-hidden">
      <div className="relative mx-auto px-5 sm:px-6" style={{ maxWidth: 1280 }}>
        <div className="text-center mb-10 md:mb-16 max-w-3xl mx-auto space-y-5">
          <h2
            className="font-heading font-extrabold text-3xl md:text-4xl leading-[1.02] tracking-tight"
            style={{ color: BRAND.darkBlue }}
          >
            Our Simple, Proven Medical Flight Process
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6 relative">
          {steps.map((step, i) => (
            <div
              key={i}
              className="group text-center relative bg-white p-7 card-hover-red process-step-card"
              style={{
                border: `1px solid ${BRAND.silver}`,
                boxShadow: "0 2px 10px rgba(23, 74, 132, 0.05)",
                borderRadius: 12,
              }}
            >
              {/* 3D isometric illustration — fully white background */}
              <div
                className="relative mx-auto mb-6 overflow-hidden"
                style={{
                  width: "100%",
                  maxWidth: 320,
                  aspectRatio: "1408 / 768",
                  backgroundColor: BRAND.white,
                  borderRadius: 8,
                }}
              >
                <Image
                  src={step.image}
                  alt={`${step.title} — 3D isometric illustration`}
                  width={1408}
                  height={768}
                  className="w-full h-full object-cover"
                  sizes="(max-width: 768px) 100vw, 320px"
                />
              </div>

              {/* Angled chevron / geometric step number */}
              <div className="relative mx-auto mb-6 process-chevron" style={{ width: 64, height: 64 }}>
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{
                    background: BRAND.darkBlue,
                    clipPath: "polygon(20% 0, 100% 0, 80% 100%, 0 100%)",
                  }}
                >
                  <span
                    className="font-heading font-extrabold text-2xl tabular-nums"
                    style={{ color: BRAND.white }}
                  >
                    {step.index}
                  </span>
                </div>
              </div>
              <h3
                className="font-heading font-bold text-lg md:text-xl mb-3 leading-snug"
                style={{ color: BRAND.darkBlue }}
              >
                {step.title}
              </h3>
              <p
                className="text-sm leading-relaxed max-w-sm mx-auto"
                style={{ color: BRAND.nearBlack }}
              >
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Section 5: Specialty Focus ───────────────────────────────────────────────
function SpecialtySection() {
  return (
    <section
      id="specialty"
      className="py-16 md:py-32 relative overflow-hidden"
      style={{ backgroundColor: BRAND.darkBlue }}
    >
      {/* Image overlay — cabin interior, dark-blue tint */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('/images/cabin-interior-709291.jpg')",
          opacity: 0.18,
        }}
      />
      {/* Solid dark-blue tint on top of the image for legibility */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: `${BRAND.darkBlue}cc` }}
      />

      <div className="relative mx-auto px-5 sm:px-6 text-center" style={{ maxWidth: 880 }}>
        <div className="space-y-6 md:space-y-7">
          <h2
            className="font-heading font-extrabold text-3xl md:text-4xl leading-[1.02] tracking-tight"
            style={{ color: BRAND.white }}
          >
            Experts in Medical Flights after Spinal Cord Injuries
          </h2>

          <p
            className="text-sm md:text-base leading-relaxed"
            style={{ color: `${BRAND.white}e6` }}
          >
            Our critical care medical flight team has extraordinary experience in assisting patients
            who have had spinal cord injuries in relocating to national Centers of Excellence for
            rehabilitation. Our team has deep expertise in precautions related to people who have
            had recent spinal cord injuries be transported as safely and effectively as possible.
          </p>

          <p
            className="text-sm md:text-base leading-relaxed font-semibold"
            style={{ color: BRAND.white }}
          >
            When spinal cord injury care matters most, the team at Critical Care MedFlight has the
            experience your loved one needs.
          </p>

          <div className="pt-4">
            <a
              href="tel:8004266557"
              className="inline-flex items-center gap-2 font-heading font-medium text-base px-8 py-4 transition-all duration-300 ease-out hover:scale-[1.03] hover:shadow-lg active:scale-[0.98] cta-red"
              style={{ backgroundColor: BRAND.red, color: BRAND.white, borderRadius: 6 }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Call Our Advisors Now
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Section 6: Advisors — strict 50/50 split ─────────────────────────────────
function AdvisorsSection() {
  const bullets = [
    "Get a fast, accurate quote for how much your medical flight will cost",
    "Help you understand your options for payments, including whether your medical insurance is likely to cover the cost of your medical flight",
    "Connect with the hospital or medical facility on both sides of the medical flight",
    "Pursue and obtain medical documentation for the flight's necessity",
    "Arrange a medical flight that works for your loved one, at the time, location, and airports that provide the best medical care for them",
    "Coordinate ground transportation to and from the airport, so your loved one has complete care for the duration of the transportation between medical facilities",
    "Identify any travel documentation needed (like visas or travel authorizations)",
  ]

  return (
    <section id="advisors" className="py-16 md:py-28 relative" style={{ backgroundColor: BRAND.white }}>
      <div className="mx-auto px-5 sm:px-6" style={{ maxWidth: 1280 }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
          {/* Left column — text */}
          <div className="flex flex-col justify-center">
            <h2
              className="font-heading font-extrabold text-3xl md:text-4xl leading-[1.02] tracking-tight mb-4"
              style={{ color: BRAND.darkBlue }}
            >
              Personal, Dedicated Medical Flight Advisors
            </h2>
            <p
              className="font-heading font-bold text-base md:text-lg mb-6 italic"
              style={{ color: BRAND.red }}
            >
              Your Medical Transportation Healthcare Advocate
            </p>
            <p
              className="text-sm md:text-base leading-relaxed mb-8"
              style={{ color: BRAND.nearBlack }}
            >
              Our team of highly-trained medical flight advisors understands every aspect of medical
              flight care coordination. They&apos;ll work with you or on your behalf to:
            </p>

            {/* Checklist — numbered chip + accent rail, rounded corners, dark blue w/ red hover rail */}
            <ul className="space-y-2.5 mb-8">
              {bullets.map((b, i) => (
                <li
                  key={i}
                  className="group flex items-stretch gap-0 transition-colors overflow-hidden"
                  style={{
                    backgroundColor: BRAND.white,
                    borderTop: `1px solid ${BRAND.silver}`,
                    borderRight: `1px solid ${BRAND.silver}`,
                    borderBottom: `1px solid ${BRAND.silver}`,
                    borderLeft: `4px solid ${BRAND.darkBlue}`,
                    boxShadow: "0 2px 8px rgba(23, 74, 132, 0.04)",
                    borderRadius: 14,
                  }}
                >
                  {/* Numbered chip */}
                  <span
                    className="flex-shrink-0 flex items-center justify-center font-heading font-extrabold text-xs md:text-sm"
                    style={{
                      width: 40,
                      minHeight: "100%",
                      backgroundColor: BRAND.darkBlue,
                      color: BRAND.white,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {/* Body text — rail turns red on hover */}
                  <span
                    className="flex-1 px-4 py-2.5 text-xs md:text-sm leading-relaxed transition-colors"
                    style={{
                      color: BRAND.nearBlack,
                      borderLeft: "1px solid transparent",
                    }}
                  >
                    {b}
                  </span>
                </li>
              ))}
            </ul>

            {/* Closing */}
            <p
              className="text-sm md:text-base leading-relaxed mb-8"
              style={{ color: BRAND.nearBlack }}
            >
              As your personal medical flight advisor, they are there for you each step of the trip.
              You can count on them to help understand your options and configure a medical flight that
              gets your loved one to the care they need — fast.
            </p>
          </div>

          {/* Right column — image (sharp 90-degree square corners) */}
          <div className="relative min-h-[420px] lg:min-h-[640px] overflow-hidden" style={{ borderRadius: 0 }}>
            <Image
              src="/images/blue-and-white-airplane-with-luggage-on-tarmac-2026-03-25-07-00-24-utc-1.jpg"
              alt="Blue and white medical flight airplane on the tarmac with luggage loaded, ready for departure"
              fill
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-cover"
              style={{ borderRadius: 0 }}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Section 7: Service Areas ─────────────────────────────────────────────────
function CoverageSection() {
  const regions = [
    {
      image: "/images/service-usa-703667.jpg",
      title: "Cross-Country Medical Flights",
      body: "We can arrange medical flights throughout the US, including Hawaii and Alaska",
    },
    {
      image: "/images/service-caribbean-703685.jpg",
      title: "Mexico & Caribbean Medical Flights",
      body: "Our teams are positioned near Mexico and the Caribbean and can frequently be there within a day",
    },
    {
      image: "/images/service-europe-703697.jpg",
      title: "Europe Medical Flights",
      body: "Whether your loved one needs to go to Europe from the US, or vice versa, our team can assist",
    },
  ]

  return (
    <section id="coverage" className="py-16 md:py-28 bg-white relative">
      <div className="relative mx-auto px-5 sm:px-6" style={{ maxWidth: 1280 }}>
        {/* Centred header + intro */}
        <div className="text-center mb-10 md:mb-16 max-w-3xl mx-auto space-y-5">
          <h2
            className="font-heading font-extrabold text-3xl md:text-4xl leading-[1.02] tracking-tight"
            style={{ color: BRAND.darkBlue }}
          >
            Worldwide Medical Flights
          </h2>
          <p
            className="text-sm md:text-base leading-relaxed"
            style={{ color: BODY }}
          >
            Critical Care MedFlight&apos;s personal medical flight advisors can help arrange a medical
            flight throughout most of the world. Whether the patient is in the US, the Caribbean,
            Mexico, Europe, South America or even Antarctica, we can arrange their medical flight.
          </p>
        </div>

        {/* 3-column image cards — rounded corners, filled blue, hover transitions to red */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {regions.map((r, i) => (
            <div
              key={i}
              className="group coverage-card coverage-card-body"
              style={{
                boxShadow: "0 4px 24px rgba(23, 74, 132, 0.12)",
                borderRadius: 12,
              }}
            >
              <div className="h-56 coverage-img-wrap">
                <div
                  className="h-full w-full coverage-img"
                  style={{ backgroundImage: `url('${r.image}')`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
              </div>
              <div className="p-6">
                <h3
                  className="font-heading font-bold text-base md:text-lg mb-2.5 leading-snug"
                  style={{ color: BRAND.white }}
                >
                  {r.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: `${BRAND.white}cc` }}
                >
                  {r.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Section 7.5: Call-Now Banner ─────────────────────────────────────────────
function CallNowBanner() {
  return (
    <section
      className="relative isolate overflow-hidden py-14 md:py-20"
      style={{ backgroundColor: BRAND.darkBlue }}
    >
      {/* USA map background — extends full bleed on all sides */}
      <div
        className="absolute inset-0 bg-cover bg-center pointer-events-none"
        style={{
          backgroundImage: "url('/images/1781904445643-0.png')",
          opacity: 0.35,
        }}
      />
      {/* Dark tint overlay so foreground copy stays legible */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundColor: `${BRAND.darkBlue}cc` }}
      />

      <div
        className="relative mx-auto px-5 sm:px-6 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-8 md:gap-10 items-center"
        style={{ maxWidth: 1080 }}
      >
        {/* Left column — phone icon + copy */}
        <div className="flex flex-col items-center text-center gap-4 md:gap-5">
          <div
            className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center"
            style={{
              border: `2px solid ${BRAND.white}`,
              borderRadius: "50%",
            }}
          >
            <svg
              className="w-5 h-5 md:w-6 md:h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              style={{ color: BRAND.white }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
          </div>
          <div>
            <p
              className="font-heading font-medium text-xs md:text-sm tracking-[0.18em] uppercase mb-2"
              style={{ color: `${BRAND.white}b3` }}
            >
              Wherever you need
            </p>
            <h2
              className="font-heading font-extrabold text-2xl sm:text-3xl md:text-4xl leading-tight mb-2 md:mb-3"
              style={{ color: BRAND.white }}
            >
              Give us a call.
            </h2>
            <p
              className="text-sm md:text-base leading-relaxed"
              style={{ color: `${BRAND.white}cc` }}
            >
              Our personal medical flight advisors help throughout the world.
            </p>
          </div>
        </div>

        {/* Center divider */}
        <div
          className="hidden md:block w-px self-stretch"
          style={{ backgroundColor: `${BRAND.white}25` }}
        />

        {/* Right column — 24/7 + phone + CTA */}
        <div className="text-center md:text-left">
          <span
            className="inline-block font-heading font-medium text-xs md:text-sm tracking-[0.18em] uppercase mb-3 md:mb-4 px-3 py-1.5"
            style={{
              color: BRAND.darkBlue,
              backgroundColor: BRAND.white,
              borderRadius: 999,
            }}
          >
            Available 24/7
          </span>
          <a
            href="tel:8004266557"
            className="inline-block font-heading font-extrabold text-[2rem] sm:text-3xl md:text-5xl leading-tight tracking-tight mb-4 md:mb-5 transition-colors hover:text-red"
            style={{ color: BRAND.white }}
          >
            800-426-6557
          </a>
          <div>
            <a
              href="#quote-form"
              className="inline-flex items-center gap-2 font-heading font-medium text-base px-8 py-4 transition-all duration-300 ease-out hover:scale-[1.03] hover:shadow-lg active:scale-[0.98] cta-red"
              style={{ backgroundColor: BRAND.red, color: BRAND.white, borderRadius: 6 }}
            >
              Request a Transfer
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Section 8: Final CTA ─────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="relative isolate overflow-hidden">
      {/* Video background */}
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster=""
        className="absolute inset-0 w-full h-full object-cover -z-20"
      >
        <source src="/videos/airplane-sunset-hero.mp4" type="video/mp4" />
      </video>

      {/* Gradient overlay — deep darkBlue that fades to transparent toward the middle
          so the warm sunset stays visible behind the copy */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            `linear-gradient(180deg, ${BRAND.darkBlue}cc 0%, ${BRAND.darkBlue}99 35%, ${BRAND.darkBlue}66 60%, ${BRAND.darkBlue}cc 100%)`,
        }}
      />
      {/* Subtle vignette to deepen the edges */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 30%, rgba(10,24,44,0.55) 100%)",
        }}
      />

      <div className="relative mx-auto px-5 sm:px-6 text-center py-14 md:py-24" style={{ maxWidth: 980 }}>
        {/* Display headline */}
        <h2
          className="font-heading font-extrabold leading-[1.1] tracking-tight mb-6 text-balance"
          style={{
            color: BRAND.white,
            fontSize: "clamp(1.5rem, 3vw, 2.25rem)",
          }}
        >
          When Every Minute Matters,
          <br />
          We Deliver.
        </h2>

        {/* Subhead */}
        <p
          className="text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-10 font-light"
          style={{ color: `${BRAND.white}e6` }}
        >
          Call our 24/7 team of personal medical flight advisors now.
          We&apos;re ready to mobilize — anywhere in the world.
        </p>

        {/* CTA */}
        <a
          href="tel:8004266557"
          className="inline-flex items-center gap-3 font-heading font-medium px-8 py-4 text-base transition-all duration-300 ease-out hover:scale-[1.03] hover:shadow-xl active:scale-[0.98] cta-red"
          style={{
            backgroundColor: BRAND.red,
            color: BRAND.white,
            borderRadius: 6,
            boxShadow: "0 14px 40px rgba(223, 70, 59, 0.35)",
          }}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          Call Now — We&apos;re Ready
        </a>
      </div>
    </section>
  )
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="py-12" style={{ backgroundColor: BRAND.nearBlack }}>
      <div className="mx-auto px-5 sm:px-6" style={{ maxWidth: 1200 }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-10">
          {/* Brand */}
          <div className="sm:col-span-2 md:col-span-2">
            <div className="mb-4">
              <Image
                src="/images/ccm-white-logo.png"
                alt="Critical Care MedFlight"
                width={180}
                height={58}
                className="w-auto h-10 object-contain"
              />
            </div>
            <p
              className="font-heading font-bold text-base mb-2"
              style={{ color: BRAND.white }}
            >
              CRITICAL CARE MEDFLIGHT Fixed-Wing Air Ambulance
            </p>
            <p
              className="text-base leading-relaxed mb-4 max-w-md"
              style={{ color: `${BRAND.white}b0` }}
            >
              Specializing in fixed-wing air ambulance services for critically ill or injured
              patients. Nationwide. Worldwide. 24/7/365.
            </p>
            <div
              className="inline-flex items-center gap-3 px-3 py-2"
              style={{
                backgroundColor: `${BRAND.white}10`,
                border: `1px solid ${BRAND.white}30`,
              }}
            >
              <Image
                src="/images/naamta-logo-500x500.png"
                alt="NAAMTA Accredited Member"
                width={36}
                height={36}
                className="w-9 h-9 object-contain"
              />
              <span
                className="text-sm font-medium"
                style={{ color: BRAND.white }}
              >
                NAAMTA Accredited Member
              </span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4
              className="font-heading font-bold text-base mb-3"
              style={{ color: BRAND.white }}
            >
              Contact
            </h4>
            <p
              className="text-base mb-1.5"
              style={{ color: `${BRAND.white}b0` }}
            >
              (800) 426-6557
            </p>
            <p
              className="text-base"
              style={{ color: `${BRAND.white}b0` }}
            >
              24/7 Coordination Center
            </p>
          </div>

          {/* Links */}
          <div>
            <h4
              className="font-heading font-bold text-base mb-3"
              style={{ color: BRAND.white }}
            >
              Links
            </h4>
            <ul className="space-y-2">
              {[
                { label: "Conditions We Serve", href: "/conditions" },
                { label: "Privacy Policy", href: "/privacy-policy" },
                { label: "Terms of Service", href: "#" },
                { label: "HIPAA Notice", href: "#" },
              ].map(({ label, href }) => (
                <li key={label}>
                  <Link
                    href={href}
                    className="text-base transition-colors"
                    style={{ color: `${BRAND.white}b0` }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = BRAND.white)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = `${BRAND.white}b0`)}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="border-t pt-6"
          style={{ borderColor: `${BRAND.white}20` }}
        >
          <p
            className="text-sm text-center"
            style={{ color: `${BRAND.white}70` }}
          >
            © 2026 Critical Care MedFlight. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function HomePage() {
  return (
    <div className="min-h-screen">
      <SiteNav active="home" />
      <main id="main-content">
        <HeroSection />
        <SkinnyFormBar />
        <TrustBar />
        <WhyUsSection />
        <ProcessSection />
        <SpecialtySection />
        <AdvisorsSection />
        <CoverageSection />
        <CallNowBanner />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  )
}
